// Import necessary styles and d3 library
import './styles/graphic.scss';
import * as d3 from 'd3';

// Define a default export function that creates a treemap visualization
export default function (props, container, isMobile) {
  // Destructure properties from props
  const {
    data,
    initialWidth,
    initialHeight,
    // width,
    // height
  } = props;

  const offset = 55;
  const length = 5;

  // Append an SVG element to the container with specified initial dimensions
  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', initialHeight);

  // Prepare input data in hierarchical format suitable for treemap visualization
  const root = d3.hierarchy(data).sum(d => d.value);

  // Compute treemap layout based on specified dimensions and padding
  d3.treemap()
    .size([initialWidth, initialHeight - 60])
    .paddingTop(5)
    .paddingRight(5)
    .paddingInner(2)
    .tile(d3.treemapSquarify.ratio(1.2))(root)
    .sort((a, b) => b.height - a.height || b.value - a.value);

  // Create a tooltip element for displaying node information on hover
  const tooltip = d3
    .select('body')
    .append('div')
    .style('position', 'absolute')
    .style('z-index', '10')
    .style('visibility', 'hidden')
    .style('background-color', 'white')
    .style('border', 'solid')
    .style('border-width', '2px')
    .style('border-color', '#4A157A')
    .style('border-radius', '5px')
    .style('padding', '5px');

  // Prepare a color scale for nodes based on group names
  const color = d3.scaleOrdinal()
    .domain(['United States', 'EU', 'Other'])
    .range(['#895FAE', '#A98CC4', '#D1C1E0']);

  const label_color = d3.scaleOrdinal()
    .domain(['United States', 'EU', 'Other'])
    .range(['#FFF', '#FFF', '#74509B']);

  // Prepare an opacity scale for nodes based on value range
  const opacity = d3.scaleLinear()
    .domain([0, 0.5])
    .range([0.5, 1]);


  // Add rectangles representing treemap nodes to the SVG
  svg
    .selectAll('rect')
    .data(root.leaves())
    .enter()
    .append('rect')
    .attr('rx', 5)
    .attr('ry', 5)
    .attr('x', d => d.x0)
    .attr('y', d => ((isMobile == true && (d.data.shortcode) == 'US') ? d.x0 + 10 : d.y0 + 30))
    .attr('width', d => d.x1 - d.x0)
    .attr('height', d => d.y1 - d.y0)
    .style('fill', d => color(d.parent.data.name))
    .on('mouseover', () => {
      tooltip.style('visibility', 'visible');
    })
    .on('mousemove', (d) => {
      const tooltipWidth = tooltip.node().getBoundingClientRect().width;
      const tooltipHeight = tooltip.node().getBoundingClientRect().height;
      let tooltipX = d3.event.pageX - tooltipWidth / 2;
      let tooltipY = d3.event.pageY - tooltipHeight - 10;
      const parentRect = svg.node().getBoundingClientRect();
      if (tooltipX < parentRect.left) {
        tooltipX = parentRect.left;
      } else if (tooltipX + tooltipWidth > parentRect.right) {
        tooltipX = parentRect.right - tooltipWidth;
      }
      if (tooltipY < parentRect.top) {
        tooltipY = parentRect.top;
      } else if (tooltipY + tooltipHeight > parentRect.bottom) {
        tooltipY = parentRect.bottom - tooltipHeight;
      }
      tooltip
        .style('top', `${tooltipY}px`)
        .style('left', `${tooltipX}px`)
        .style('pointer-events', 'none')
        .html(`<b>${d.data.name}:</b> ${d.data.pc.toFixed(1)}%<br>
                $${((d.data.value.toFixed(0) > 1) ? `${(d.data.value).toFixed(1)} billion` : `${(d.data.value).toFixed(2)} billion`)}`);
    })
    .on('mouseout', () => {
      tooltip.style('visibility', 'hidden');
    });

  // Add text labels for treemap nodes
  svg
    .selectAll('text')
    .data(root.leaves())
    .enter()
    .append('text')
    .attr('x', d => d.x0 + (d.x1 - d.x0) / 2)
    .attr('y', (d) => {
      if (isMobile == true && (d.data.name) == 'United States') {
        return d.y0 + ((d.y1 + 10) - d.y0) / 2;
      } if ((d.data.pc) > 1) {
        return d.y0 + ((d.y1 + offset) - d.y0) / 2;
      }
      return d.y0 + ((d.y1 + 68) - d.y0) / 2;
    })
    .text((d) => {
      if ((d.data.name) == 'United States' || (d.data.name) == 'United Kingdom' || (d.data.name) == 'Italy') {
        return d.data.name;
      } if ((String(d.data.name).length) > 10 && (d.data.pc) > 1.5) {
        return d.data.shortcode;
      } if (isMobile == true && (d.data.pc) < 3 && (d.data.pc) > 0.5) {
        return d.data.shortcode;
      } if ((d.data.pc) > 1.5) {
        return d.data.name;
      } if ((d.data.pc) < 1.5 && (d.data.pc) > 0.5) {
        return d.data.shortcode;
      }
      return '';
    })
    .attr('font-size', (d) => {
      if ((d.data.pc) > 40) {
        return '20px';
      } if (isMobile == true) {
        return '13px';
      }
      return '16px';
    })
    .style('fill', d => label_color(d.parent.data.name))
    .style('pointer-events', 'none')
    .style('text-anchor', 'middle')
    .style('font-weight', 'bold');

  // Add percentage value labels for treemap nodes
  svg
    .selectAll('vals')
    .data(root.leaves())
    .enter()
    .append('text')
    .attr('x', d => d.x0 + (d.x1 - d.x0) / 2)
    .attr('y', d => ((isMobile == true && (d.data.shortcode) == 'US') ? d.y0 + ((d.y1 + 10) - d.y0) / 2 : d.y0 + (((d.y1 + offset) - d.y0) / 2)) + 16)
    .text((d) => {
      if ((d.data.pc) > 1) {
        return `${d.data.pc.toFixed(0)}%`;
      }
      return '';
    })
    .attr('font-size', '13px')
    .style('fill', d => label_color(d.parent.data.name))
    .style('pointer-events', 'none')
    .style('text-anchor', 'middle');

  // Add title text for the three main groups
  svg
    .selectAll('titles')
    .data(root.descendants().filter(d => d.depth == 1))
    .enter()
    .append('text')
    .attr('x', d => (d.x0 + d.x1) / 2)
    .attr('y', d => (d.data.name == 'EU' ? d.y0 + 18 : d.y1 + offset))
    .text((d) => {
      if (d.data.name == 'United States') {
        return '';
      } if (d.data.name == 'EU') {
        return 'European Union: 32%';
      }
      return 'Other: 22%';
    })
    .attr('font-size', '14px')
    .style('text-anchor', 'middle')
    .attr('fill', '#262626');


  // Add the brackets for groups, each one is made up of three different lines. two end pieces and the horizontal
  svg.selectAll('hr')
    .data(root.descendants().filter(d => d.depth == 1 && d.data.name != 'United States'))
    .enter()
    .append('line')
    .style('stroke', '#BCBCBC')
    .style('stroke-width', 1)
    .attr('x1', d => d.x0 + 2)
    .attr('y1', d => (d.data.name == 'EU' ? d.y0 + 27 : d.y1 + 40))
    .attr('x2', d => d.x1 - 7)
    .attr('y2', d => (d.data.name == 'EU' ? d.y0 + 27 : d.y1 + 40));

  svg.selectAll('hr')
    .data(root.descendants().filter(d => d.depth == 1 && d.data.name != 'United States'))
    .enter()
    .append('line')
    .style('stroke', '#BCBCBC')
    .style('stroke-width', 1)
    .attr('x1', d => d.x0 + 2)
    .attr('y1', d => (d.data.name == 'EU' ? d.y0 + ((offset / 2) + length) : d.y1 + 40))
    .attr('x2', d => d.x0 + 2)
    .attr('y2', d => (d.data.name == 'EU' ? d.y0 + (offset / 2) : d.y1 + 34));

  svg.selectAll('hr')
    .data(root.descendants().filter(d => d.depth == 1 && d.data.name != 'United States'))
    .enter()
    .append('line')
    .style('stroke', '#BCBCBC')
    .style('stroke-width', 1)
    .attr('x1', d => d.x1 - 7)
    .attr('y1', d => (d.data.name == 'EU' ? d.y0 + (offset / 2) : d.y1 + 40))
    .attr('x2', d => d.x1 - 7)
    .attr('y2', d => (d.data.name == 'EU' ? d.y0 + ((offset / 2) + length) : d.y1 + 34));
}
